/**
 * KYC + License DTOs exposed by operator-service `/api/kyc/*` and
 * `/api/admin/license-pricing/*`.
 */

import { defensiveStatusMeta } from '../helpers/status-meta';
import type { LicenseTier, OperatorKycStatus } from './operator.types';
import type { UserRef } from './user.types';

// ── KYC ────────────────────────────────────────────────────────────────

export type KycDocumentStatus = 'PENDING' | 'VALIDATED' | 'REJECTED' | 'EXPIRED';
export type KycDocumentType =
  | 'IDENTITY_CARD'
  | 'PASSPORT'
  | 'DRIVER_LICENSE'
  | 'BUSINESS_REGISTRATION'
  | 'VEHICLE_REGISTRATION'
  | 'PROOF_OF_ADDRESS'
  | 'OTHER';
export type KycSubjectType = 'OWNER' | 'DRIVER' | 'OPERATOR';

export interface KycDocument {
  // ── BaseEntity ───────────────────────────────────────────────────────
  id: number;
  createdAt?: string | null;
  updatedAt?: string | null;
  // ── domain ────────────────────────────────────────────────────────────
  subjectType: KycSubjectType | string;
  subjectRef: number;
  /**
   * Nom lisible du sujet (`businessName` pour un OPERATOR), résolu côté back
   * (`KycDocumentView`, résolution locale batchée).
   *
   * ⚠️ Optionnel : `null` hors OPERATOR (pas de résolution cross-service), et
   * **absent des réponses `validate`/`reject`** qui renvoient le `KycDocument`
   * brut → toujours **fusionner** une réponse de décision dans la ligne
   * existante plutôt que la remplacer, sinon le nom disparaît. Repli d'affichage
   * dans tous les cas : « Opérateur #7 ».
   */
  subjectName?: string | null;
  /** Gérant du sujet (avatar + nom). Mêmes réserves que `subjectName`. */
  user?: UserRef | null;
  documentType: KycDocumentType;
  documentUrl: string;
  issueDate?: string | null;
  expiryDate?: string | null;
  kycStatus: KycDocumentStatus;
  validatedAt?: string | null;
  validatedByAdminId?: number | null;
  rejectionReason?: string | null;
  lastReminderAt?: string | null;
  lastReminderDayMark?: number | null;
}

/** Body of `POST /api/kyc/{id}/reject`. */
export interface KycRejectRequest {
  reason: string;
}

// ── License pricing ────────────────────────────────────────────────────

export interface LicensePricing {
  id: number;
  createdAt?: string | null;
  updatedAt?: string | null;
  tier: LicenseTier;
  price: number;
  /** `null` = durée illimitée (pas d'expiration). */
  durationDays: number | null;
  /** `null` = nombre de prestataires illimité. */
  maxPrestataires?: number | null;
  currency: string;
  description?: string | null;
}

/** Body of `PUT /api/admin/license-pricing/{tier}`. */
export interface LicensePricingRequest {
  price: number;
  /** `null` = durée illimitée. */
  durationDays: number | null;
  /** `null` = prestataires illimités. */
  maxPrestataires: number | null;
  description?: string | null;
}

// ── UI helpers ─────────────────────────────────────────────────────────

/**
 * Meta d'affichage du **dossier** KYC opérateur (#66) — distinct du statut
 * par document ci-dessous. Le dossier gate le recrutement de prestataires ;
 * le document gate le suivi d'expiration.
 */
export const OPERATOR_KYC_STATUS_META = defensiveStatusMeta<OperatorKycStatus>({
  PENDING: { label: 'À soumettre', palette: 'gray' },
  UNDER_REVIEW: { label: 'En cours de validation', palette: 'warning' },
  VALIDATED: { label: 'Validé', palette: 'brand' },
  REJECTED: { label: 'Rejeté', palette: 'error' },
});

export const KYC_STATUS_META = defensiveStatusMeta<KycDocumentStatus>({
  PENDING: { label: 'En attente', palette: 'warning' },
  VALIDATED: { label: 'Validé', palette: 'brand' },
  REJECTED: { label: 'Rejeté', palette: 'error' },
  EXPIRED: { label: 'Expiré', palette: 'gray' },
});

export const KYC_DOCUMENT_TYPE_LABEL: Record<KycDocumentType, string> = {
  IDENTITY_CARD: "Carte d'identité",
  PASSPORT: 'Passeport',
  DRIVER_LICENSE: 'Permis de conduire',
  BUSINESS_REGISTRATION: 'Registre commerce',
  VEHICLE_REGISTRATION: 'Carte grise',
  PROOF_OF_ADDRESS: 'Justificatif de domicile',
  OTHER: 'Autre',
};

export const KYC_SUBJECT_TYPE_LABEL: Record<string, string> = {
  OWNER: 'Bailleur',
  DRIVER: 'Chauffeur',
  OPERATOR: 'Opérateur',
};
